import {
  DEFAULT_DATE_FORMAT_FNS,
  DefaultSchoolYear,
  SchoolYear,
  useGetSchoolYears,
} from '@schooly/api';
import { useAuth } from '@schooly/components/authentication';
import { findSchoolYearForDate } from '@schooly/components/filters';
import { newDateTimezoneOffset } from '@schooly/utils/date';
import {
  closestIndexTo,
  endOfDay,
  isBefore,
  isFuture,
  isWithinInterval,
  parse,
  parseISO,
  startOfToday,
} from 'date-fns';
import { useCallback, useMemo } from 'react';

const useSchoolYears = () => {
  const { schoolId } = useAuth();
  const { data, isLoading, refetch, isFetching } = useGetSchoolYears(schoolId ?? '', {
    enabled: !!schoolId,
  });

  const items = data?.school_years;

  const getSchoolYearById = useCallback(
    (schoolYearId?: string) =>
      schoolYearId ? items?.find((item) => item.id === schoolYearId) : undefined,
    [items],
  );

  const getSchoolYearByDate = useCallback(
    (date: Date) => {
      if (!items) return undefined;

      return findSchoolYearForDate(items, date);
    },
    [items],
  );

  const getDefaultValidity = useCallback(
    (forwardOnly?: boolean): DefaultSchoolYear | undefined => {
      if (!items) return undefined;
      return getDefaultYear(items, forwardOnly);
    },
    [items],
  );

  const defaultValidity = useMemo(
    () => (items ? getDefaultValidity() : undefined),
    [getDefaultValidity, items],
  );

  const nextSchoolYear = useMemo(() => {
    const futureYears = items?.filter((y) => isFuture(parseISO(y.start)));
    const startDates = futureYears?.map((year) => parseISO(year.start)) ?? [];
    const startIndex = closestIndexTo(newDateTimezoneOffset(), startDates);
    const startYear = futureYears && startIndex != null ? futureYears[startIndex] : undefined;
    return startYear ?? undefined;
  }, [items]);

  const isPreviousSchoolYear = useCallback((schoolYear: SchoolYear): boolean => {
    const today = startOfToday();
    return isBefore(newDateTimezoneOffset(schoolYear.end), today);
  }, []);

  return {
    schoolYears: items ?? [],
    isLoading: isLoading,
    isFetching: isFetching,
    fetchingSilent: isLoading,
    defaultValidity,
    nextSchoolYear,
    getSchoolYearByDate,
    getSchoolYearById,
    getDefaultValidity,
    isPreviousSchoolYear,
    refetch,
  };
};

export default useSchoolYears;

export const getDefaultYear = (years: SchoolYear[], forwardOnly?: boolean) => {
  if (!years.length) return undefined;

  const actualYear = years.find((year) => {
    return isWithinInterval(new Date(), {
      start: parseISO(year.start),
      end: endOfDay(parse(year.end, DEFAULT_DATE_FORMAT_FNS, new Date())),
    });
  });

  if (actualYear) {
    return { ...actualYear, isActual: true };
  }

  // otherwise, return closest year to Today

  if (forwardOnly) {
    // look among future years only

    const startDates = years.map((year) => parseISO(year.start));
    const startIndex = closestIndexTo(new Date(), startDates);
    const startYear = startIndex != null ? years[startIndex] : undefined;

    return startYear ? { ...startYear, isActual: false } : undefined;
  }

  // look among both, past and future years

  const startDates = years.map((year) => parseISO(year.start));
  const endDates = years.map((year) => parseISO(year.end));

  // find the closest years in both directions, forward and backward
  const startIndex = closestIndexTo(new Date(), startDates);
  const endIndex = closestIndexTo(new Date(), endDates);

  // closest future year
  const startYear = startIndex != null ? years[startIndex] : undefined;
  // closest past year
  const endYear = endIndex != null ? years[endIndex] : undefined;

  if (!startYear && !endYear) {
    return undefined;
  }

  // return one if only one is found
  if (!startYear) {
    return { ...endYear!, isActual: false };
  } else if (!endYear) {
    return { ...startYear!, isActual: false };
  }

  // now define which one is closer
  const dates = [parseISO(startYear.start), parseISO(endYear.end)];
  const index = closestIndexTo(new Date(), dates);

  switch (index) {
    case 0:
      return { ...startYear, isActual: false };
    case 1:
      return { ...endYear, isActual: false };
    default:
      return undefined;
  }
};
